import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { tr } from 'date-fns/locale';
import { exportRaporExcel } from '../utils/raporExcel';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';

export default function HaftalikRaporlar({ sube }) {
  const [rapor, setRapor] = useState({
    satis: { nakit: 0, havale: 0, kart: 0, toplam: 0, adet: 0 },
    alis: { nakit: 0, havale: 0, kart: 0, toplam: 0, adet: 0 },
    giris: { nakit: 0, havale: 0, kart: 0, toplam: 0, adet: 0 },
    cikis: { nakit: 0, havale: 0, kart: 0, toplam: 0, adet: 0 },
    toplamIslemSayisi: 0,
    kasayaGirenNakit: 0,
    kategoriler: {},
    islemler: []
  });
  const [loading, setLoading] = useState(true);

  const subeId = sube?.toLowerCase().replace(/ /g, '_') || '';

  // BU HAFTANIN BAŞLANGIÇ VE BİTİŞİ (Pazartesi 00:00 - Pazar 23:59:59)
  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  weekStart.setHours(0, 0, 0, 0);
  const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
  weekEnd.setHours(23, 59, 59, 999);

  const urunler = {
    Sarrafiye: [
      'Eski Ziynet Çeyrek', 'Yeni Ziynet Çeyrek', 'Eski Ziynet Yarım', 'Yeni Ziynet Yarım',
      'Eski Ziynet Tam', 'Yeni Ziynet Tam', "Eski Ziynet 2.5'lu Gremse", "Yeni Ziynet 2.5'lu Gremse",
      'Eski Ziynet Beşli', 'Yeni Ziynet Beşli', 'Cumhuriyet Ata Çeyrek', 'Cumhuriyet Ata Yarım',
      'Cumhuriyet Ata', "Cumhuriyet Ata 2.5'lu", 'Cumhuriyet Ata Beşli', 'Reşat',
      "Reşat 2.5'lu", 'Reşat Beşli'
    ],
    'Gram Altın': [
      '1 Gram 22 Ayar', '0.50 Gram 22 Ayar', '0.25 Gram 22 Ayar', '1 Gram 24 Ayar',
      '0.50 Gram 24 Ayar', '0.25 Gram 24 Ayar', '2.50 Gram 24 Ayar', '5 Gram 24 Ayar',
      '10 Gram 24 Ayar', '20 Gram 24 Ayar', '50 Gram 24 Ayar', '100 Gram 24 Ayar'
    ],
    'Takı Ürün': [
      '22 Ayar Bilezik', '14 Ayar Bilezik', '8 Ayar Bilezik', '22 Ayar Takı',
      '21 Ayar Takı', '18 Ayar Takı', '14 Ayar Takı', '8 Ayar Takı'
    ],
    Hurda: [
      '24 Ayar Hurda', '22 Ayar Bilezik Hurda', '22 Ayar Hurda', '21 Ayar Hurda',
      '18 Ayar Hurda', '14 Ayar Hurda', '8 Ayar Hurda'
    ],
    Döviz: [
      'TL - Türk Lirası', 'USD - Amerikan Doları', 'EUR - Euro', 'GBP - İngiliz Sterlini',
      'CHF - İsviçre Frangı', 'AUD - Avustralya Doları', 'CAD - Kanada Doları',
      'SAR - Suudi Arabistan Riyali', 'AED - Birleşik Arap Emirlikleri Dirhemi',
      'QAR - Katar Riyali', 'MAD - Fas Dirhemi', 'JOD - Ürdün Dinarı',
      'KWD - Kuveyt Dinarı', 'CNY - Çin Yuanı', 'RUB - Rus Rublesi', 'BGN - Bulgar Levası'
    ]
  };

  const kategorilerListe = Object.keys(urunler);

  useEffect(() => {
    if (!subeId) {
      setLoading(false);
      return;
    }

    const fetchRapor = async () => {
      try {
        const islemlerRef = collection(db, 'subeler', subeId, 'islemler');
        const q = query(islemlerRef, orderBy('timestamp', 'desc'));
        const snapshot = await getDocs(q);

        const islemler = snapshot.docs
          .map(doc => {
            const data = doc.data();
            const tarih = data.timestamp?.toDate?.(); // ZORUNLU toDate()
            return { id: doc.id, ...data, tarih };
          })
          .filter(islem => {
            const tarih = islem.timestamp?.toDate?.(); // TEKRAR DÖNÜŞTÜR
            return tarih && tarih >= weekStart && tarih <= weekEnd;
          });

        const satis = { nakit: 0, havale: 0, kart: 0, toplam: 0, adet: 0 };
        const alis = { nakit: 0, havale: 0, kart: 0, toplam: 0, adet: 0 };
        const giris = { nakit: 0, havale: 0, kart: 0, toplam: 0, adet: 0 };
        const cikis = { nakit: 0, havale: 0, kart: 0, toplam: 0, adet: 0 };
        let kasayaGirenNakit = 0;

        const kategoriler = {};
        kategorilerListe.forEach(kat => {
          kategoriler[kat] = { urunler: {} };
          urunler[kat].forEach(urunAdi => {
            kategoriler[kat].urunler[urunAdi] = { satis: 0, alis: 0, giris: 0, cikis: 0, net: 0 };
          });
        });

        islemler.forEach(islem => {
          const n = Number(islem.nakit) || 0;
          const h = Number(islem.havaleEft) || 0;
          const k = Number(islem.krediKarti) || 0;
          const toplam = n + h + k;

          const islemKategori = islem.kategori || 'Genel';
          const urunAdi = islem.urun || 'Bilinmeyen Ürün';

          const kullanilacakAlan = ['Takı Ürün', 'Hurda'].includes(islemKategori) ? 'gram' : 'adet';
          const deger = Number(islem[kullanilacakAlan]) || 0;

          const urunListesi = Array.isArray(islem.urunler) ? islem.urunler : [];

          if (urunListesi.length > 0) {
            urunListesi.forEach(urun => {
              const uAdi = urun.urunAdi || 'Bilinmeyen Ürün';
              const uDeger = Number(urun[kullanilacakAlan]) || 0;

              if (kategoriler[islemKategori]?.urunler[uAdi] !== undefined) {
                switch (islem.islemTuru) {
                  case 'Satış':  kategoriler[islemKategori].urunler[uAdi].satis += uDeger; break;
                  case 'Alış':   kategoriler[islemKategori].urunler[uAdi].alis += uDeger; break;
                  case 'Giriş':  kategoriler[islemKategori].urunler[uAdi].giris += uDeger; break;
                  case 'Çıkış':  kategoriler[islemKategori].urunler[uAdi].cikis += uDeger; break;
                }
              }
            });
          } 
          else if (kategorilerListe.includes(islemKategori) && kategoriler[islemKategori].urunler[urunAdi] !== undefined) {
            switch (islem.islemTuru) {
              case 'Satış':  kategoriler[islemKategori].urunler[urunAdi].satis += deger; break;
              case 'Alış':   kategoriler[islemKategori].urunler[urunAdi].alis += deger; break;
              case 'Giriş':  kategoriler[islemKategori].urunler[urunAdi].giris += deger; break;
              case 'Çıkış':  kategoriler[islemKategori].urunler[urunAdi].cikis += deger; break;
            }
          }

          switch (islem.islemTuru) {
            case 'Satış':
              satis.nakit += n;
              satis.havale += h;
              satis.kart += k;
              satis.toplam += toplam;
              satis.adet += 1;
              kasayaGirenNakit += n;
              break;
            case 'Alış':
              alis.nakit += n;
              alis.havale += h;
              alis.kart += k;
              alis.toplam += toplam;
              alis.adet += 1;
              break;
            case 'Giriş':
              giris.nakit += n;
              giris.havale += h;
              giris.kart += k;
              giris.toplam += toplam;
              giris.adet += 1;
              kasayaGirenNakit += n;
              break;
            case 'Çıkış':
              cikis.nakit += n;
              cikis.havale += h;
              cikis.kart += k;
              cikis.toplam += toplam;
              cikis.adet += 1;
              break;
          }
        });

        kategorilerListe.forEach(kat => {
          const isGramKategori = ['Takı Ürün', 'Hurda'].includes(kat);
          Object.keys(kategoriler[kat].urunler).forEach(urunAdi => {
            const u = kategoriler[kat].urunler[urunAdi];
            const girisToplam = u.alis + u.giris;
            const cikisToplam = u.satis + u.cikis;
            const net = girisToplam - cikisToplam;
            u.net = isGramKategori ? Number(net.toFixed(2)) : net;
          });
        });

        const toplamIslemSayisi = satis.adet + alis.adet + giris.adet + cikis.adet;

        setRapor({
          satis, alis, giris, cikis,
          toplamIslemSayisi,
          kasayaGirenNakit,
          kategoriler,
          islemler
        });
      } catch (error) {
        console.error('Rapor hatası:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRapor();
  }, [subeId]);

  const pieData = [
    { name: 'Satış', value: rapor.satis.adet, color: '#e65100' },
    { name: 'Alış', value: rapor.alis.adet, color: '#c62828' },
    { name: 'Giriş', value: rapor.giris.adet, color: '#2e7d32' },
    { name: 'Çıkış', value: rapor.cikis.adet, color: '#1565c0' },
  ].filter(item => item.value > 0);

  const exportPDF = () => { /* aynı */ };
  const exportExcel = () => exportRaporExcel(rapor, {
    dosyaAdi: `Haftalik-Rapor-${subeId}-${format(new Date(), 'yyyy-MM-dd')}.xlsx`,
    tarihFormati: 'dd.MM HH:mm',
    tarihBasligi: 'Tarih'
  });

  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><Typography>Yükleniyor...</Typography></Box>;
  if (!subeId) return <Box sx={{ p: 3, textAlign: 'center' }}><Typography color="error">Şube seçilmedi.</Typography></Box>;

  return (
    <Box className="hr-page" sx={{ p: 1.4, background: 'linear-gradient(180deg, #f8fafd 0%, #eef3f9 100%)', minHeight: 'calc(100vh - 22px)', borderRadius: '12px', border: '1px solid #d9e2ee' }}>
      <Paper className="hr-header-paper" sx={{ p: 0.8, mb: 1.2, border: '1px solid #dce5f1', borderRadius: '10px', boxShadow: '0 6px 12px rgba(15,23,42,0.06)' }}>
        <Box className="hr-header-bar" sx={{ bgcolor: '#17385b', borderRadius: '8px', px: 1, py: 0.6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.9 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Box sx={{ width: 24, height: 24, borderRadius: '6px', bgcolor: '#284d77', color: '#fff', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>▦</Box>
            <Box>
              <Typography sx={{ color: '#fff', fontSize: '18px', fontWeight: 800, lineHeight: 1.05 }}>Haftalık rapor</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', fontWeight: 600, lineHeight: 1.1, mt: 0.45 }}>
                {format(weekStart, 'dd MMMM', { locale: tr })} - {format(weekEnd, 'dd MMMM yyyy', { locale: tr })} • {sube}
              </Typography>
            </Box>
          </Box>
          <Box className="hr-header-actions" sx={{ display: 'flex', gap: 0.6 }}>
            <Button variant="contained" size="small" onClick={exportPDF} sx={{ minWidth: 54, textTransform: 'none', borderRadius: '6px', fontSize: '10px', py: 0.3, bgcolor: '#fff', color: '#17385b', fontWeight: 700 }}>PDF</Button>
            <Button variant="contained" size="small" onClick={exportExcel} sx={{ minWidth: 54, textTransform: 'none', borderRadius: '6px', fontSize: '10px', py: 0.3, bgcolor: '#fff', color: '#17385b', fontWeight: 700 }}>Excel</Button>
          </Box>
        </Box>

        <Box
          className="hr-summary-grid"
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 0.92fr)) minmax(160px, 0.84fr) minmax(140px, 0.8fr)',
            columnGap: 2.8,
            rowGap: 1.3,
            alignItems: 'stretch',
            width: '100%'
          }}
        >
          {[
            { title: 'SATIŞ', count: rapor.satis.adet, color: '#b45309', border: '#b45309', data: rapor.satis, icon: <TrendingUpIcon sx={{ fontSize: 30, color: '#b45309' }} /> },
            { title: 'ALIŞ', count: rapor.alis.adet, color: '#b91c1c', border: '#b91c1c', data: rapor.alis, icon: <TrendingDownIcon sx={{ fontSize: 30, color: '#b91c1c' }} /> },
            { title: 'GİRİŞ', count: rapor.giris.adet, color: '#0f766e', border: '#0f766e', data: rapor.giris, icon: <LoginIcon sx={{ fontSize: 30, color: '#0f766e' }} /> },
            { title: 'ÇIKIŞ', count: rapor.cikis.adet, color: '#2563eb', border: '#2563eb', data: rapor.cikis, icon: <LogoutIcon sx={{ fontSize: 30, color: '#2563eb' }} /> },
          ].map((item) => (
            <Paper
              key={item.title}
              className="hr-summary-card"
              sx={{
                width: '100%',
                minWidth: 0,
                overflow: 'hidden',
                height: 128,
                border: '1px solid #d3e2f8',
                borderLeft: `4px solid ${item.border}`,
                borderRadius: '12px',
                boxShadow: 'none',
                display: 'flex',
                background: '#fff'
              }}
            >
              <Box sx={{ width: 52, borderRight: '1px solid #e5edf8', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fffaf6' }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', border: `1px solid ${item.border}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
                  {item.icon}
                </Box>
              </Box>
              <Box className="hr-summary-card-body" sx={{ flex: 1, px: 1, py: 0.7, minWidth: 0 }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 800, color: '#7c8eaa', letterSpacing: '0.8px' }}>{item.title}</Typography>
                <Typography sx={{ fontSize: '24px', fontWeight: 900, color: '#0f2442', lineHeight: 1.05 }}>{item.count} <span style={{ fontSize: '14px', fontWeight: 700 }}>işlem</span></Typography>
                <Box sx={{ mt: 0.35, background: '#f8fafc', border: '1px solid #e7edf6', borderRadius: '8px', px: 0.8, py: 0.35 }}>
                  <Typography sx={{ fontSize: '12px', color: '#4f6483', fontWeight: 700, lineHeight: 1.25 }}>Nakit <b style={{ float: 'right', color: '#0f2442' }}>{item.data.nakit.toLocaleString('tr-TR')} ₺</b></Typography>
                  <Typography sx={{ fontSize: '12px', color: '#4f6483', fontWeight: 700, lineHeight: 1.25 }}>Havale/EFT <b style={{ float: 'right', color: '#0f2442' }}>{item.data.havale.toLocaleString('tr-TR')} ₺</b></Typography>
                  <Typography sx={{ fontSize: '12px', color: '#4f6483', fontWeight: 700, lineHeight: 1.25 }}>Kredi kartı <b style={{ float: 'right', color: '#0f2442' }}>{item.data.kart.toLocaleString('tr-TR')} ₺</b></Typography>
                </Box>
                <Typography sx={{ fontSize: '16px', color: '#0f2442', mt: 0.35, fontWeight: 900, lineHeight: 1 }}>Toplam {item.data.toplam.toLocaleString('tr-TR')} ₺</Typography>
              </Box>
            </Paper>
          ))}

          <Box className="hr-stats-col" sx={{ width: '100%', minWidth: 0, display: 'grid', gap: 0.9 }}>
            <Paper sx={{ p: 0.75, border: '1px solid #dce5f1', borderLeft: '3px solid #8b5cf6', borderRadius: '8px', height: 49 }}>
              <Typography sx={{ fontSize: '10px', color: '#6b7280', fontWeight: 800 }}>TOPLAM İŞLEM ADEDİ</Typography>
              <Typography sx={{ fontSize: '26px', color: '#6d28d9', fontWeight: 900, lineHeight: 1 }}>{rapor.toplamIslemSayisi}</Typography>
            </Paper>
            <Paper sx={{ p: 0.75, border: '1px solid #dce5f1', borderLeft: '3px solid #059669', borderRadius: '8px', height: 49 }}>
              <Typography sx={{ fontSize: '10px', color: '#6b7280', fontWeight: 800 }}>KASAYA GİREN NAKİT</Typography>
              <Typography sx={{ fontSize: '22px', color: '#065f46', fontWeight: 900, lineHeight: 1 }}>{rapor.kasayaGirenNakit.toFixed(0)} TL</Typography>
            </Paper>
          </Box>

          <Paper className="hr-pie-card" sx={{ width: '100%', minWidth: 0, overflow: 'hidden', height: 104, p: 0.75, border: '1px solid #dce5f1', borderRadius: '8px', boxShadow: 'none' }}>
            <Typography sx={{ fontSize: '10px', color: '#6b7280', fontWeight: 800, mb: 0.2 }}>İŞLEM DAĞILIMI</Typography>
            <ResponsiveContainer width="100%" height={84}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={14} outerRadius={28} paddingAngle={2} dataKey="value">
                  {pieData.map((entry, i) => (
                    <Cell key={`cell-${i}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Box>
      </Paper>

      <Typography className="hr-categories-title" sx={{ fontSize: '13px', fontWeight: 800, color: '#102a43', mb: 0.6 }}>Kategori Özeti</Typography>
      <Box
        className="hr-categories-grid"
        sx={{
          display: 'grid',
          gridTemplateColumns: '0.98fr 0.86fr 1.1fr 1.06fr',
          gridAutoRows: '255px',
          columnGap: 1.8,
          rowGap: 2.6,
          alignItems: 'start',
          mb: 2.1,
          width: '96.5%',
          mx: 'auto'
        }}
      >
        {kategorilerListe.map(kategori => {
          const katData = rapor.kategoriler[kategori];
          const isGramKategori = ['Takı Ürün', 'Hurda'].includes(kategori);
          const layoutSx =
            kategori === 'Sarrafiye'
              ? { gridColumn: '1', gridRow: '1 / span 2' }
              : kategori === 'Gram Altın'
                ? { gridColumn: '2', gridRow: '1 / span 2' }
                : kategori === 'Takı Ürün'
                  ? { gridColumn: '3', gridRow: '1' }
                  : kategori === 'Hurda'
                    ? { gridColumn: '3', gridRow: '2' }
                    : { gridColumn: '4', gridRow: '1 / span 2' };

          return (
            <Box key={kategori} className="hr-category-item" sx={{ ...layoutSx }}>
              <Paper className="hr-category-paper" elevation={2} sx={{
                width: '100%',
                height: '100%',
                p: '6px',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #dce5f1',
                borderRadius: '10px',
                boxShadow: '0 8px 14px rgba(15,23,42,0.06)',
                background: 'linear-gradient(180deg, #ffffff 0%, #f9fbff 100%)'
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5, color: '#fff', fontSize: '13px', background: '#18385c', borderRadius: '6px', px: 1, py: 0.55 }}>
                  {kategori}
                </Typography>
                <Box className="hr-category-table-wrap" sx={{ flex: 1, overflowY: 'auto' }}>
                  <table className="hr-category-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f0f5fb', color: '#18385c' }}>
                        <th style={{ padding: '5px 4px', textAlign: 'left', fontWeight: 800, fontSize: '12px' }}>Ürün</th>
                        <th style={{ padding: '5px 4px', textAlign: 'right', fontWeight: 800, fontSize: '12px' }}>S</th>
                        <th style={{ padding: '5px 4px', textAlign: 'right', fontWeight: 800, fontSize: '12px' }}>A</th>
                        <th style={{ padding: '5px 4px', textAlign: 'right', fontWeight: 800, fontSize: '12px' }}>G</th>
                        <th style={{ padding: '5px 4px', textAlign: 'right', fontWeight: 800, fontSize: '12px' }}>Ç</th>
                        <th style={{ padding: '5px 4px', textAlign: 'right', fontWeight: 800, fontSize: '12px' }}>N</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(katData.urunler).map(([urunAdi, u]) => (
                        <tr key={urunAdi} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '4px 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100px', fontSize: '12px' }} title={urunAdi}>
                            {urunAdi}
                          </td>
                          <td style={{ padding: '4px 4px', textAlign: 'right', color: u.satis > 0 ? 'red' : 'inherit', fontWeight: u.satis > 0 ? 'bold' : 'normal', fontSize: '12px' }}>
                            {isGramKategori ? u.satis.toFixed(2) : u.satis}
                          </td>
                          <td style={{ padding: '4px 4px', textAlign: 'right', color: u.alis > 0 ? 'green' : 'inherit', fontWeight: u.alis > 0 ? 'bold' : 'normal', fontSize: '12px' }}>
                            {isGramKategori ? u.alis.toFixed(2) : u.alis}
                          </td>
                          <td style={{ padding: '4px 4px', textAlign: 'right', color: u.giris > 0 ? 'green' : 'inherit', fontWeight: u.giris > 0 ? 'bold' : 'normal', fontSize: '12px' }}>
                            {isGramKategori ? u.giris.toFixed(2) : u.giris}
                          </td>
                          <td style={{ padding: '4px 4px', textAlign: 'right', color: u.cikis > 0 ? 'red' : 'inherit', fontWeight: u.cikis > 0 ? 'bold' : 'normal', fontSize: '12px' }}>
                            {isGramKategori ? u.cikis.toFixed(2) : u.cikis}
                          </td>
                          <td style={{ padding: '4px 4px', textAlign: 'right', fontWeight: 'bold', color: u.net > 0 ? 'green' : u.net < 0 ? 'red' : 'inherit', fontSize: '12px' }}>
                            {isGramKategori ? u.net.toFixed(2) : u.net}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Paper>
            </Box>
          );
        })}
      </Box>

      <Paper className="hr-transactions-paper" elevation={3} sx={{ p: 1, border: '1px solid #dce5f1', borderRadius: '10px', boxShadow: '0 8px 14px rgba(15,23,42,0.06)' }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 800, color: '#fff', fontSize: '13px', background: '#18385c', borderRadius: '6px', px: 1, py: 0.5 }}>Bu Haftanın İşlemleri</Typography>
        {rapor.islemler.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>Bu hafta işlem yok.</Typography>
        ) : (
          <Box className="hr-transactions-wrap" sx={{ overflowX: 'auto' }}>
            <table className="hr-transactions-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#1a4971', color: 'white' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Tarih</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Müşteri</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Tür</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Kategori</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Ürün</th>
                  <th style={{ padding: '12px', textAlign: 'right' }}>Adet</th>
                  <th style={{ padding: '12px', textAlign: 'right' }}>Gram</th>
                  <th style={{ padding: '12px', textAlign: 'right' }}>Nakit</th>
                  <th style={{ padding: '12px', textAlign: 'right' }}>Havale/EFT</th>
                  <th style={{ padding: '12px', textAlign: 'right' }}>Kredi Kartı</th>
                  <th style={{ padding: '12px', textAlign: 'right' }}>Toplam</th>
                </tr>
              </thead>
              <tbody>
                {rapor.islemler.map(i => {
                  const adet = Number(i.adet) || 0;
                  const gram = Number(i.gram) || 0;
                  const isTL = i.kategori === 'Döviz' && i.urun === 'TL - Türk Lirası';
                  const finalAdet = isTL ? (Number(i.nakit) || 0) : adet;
                  const finalGram = isTL ? 0 : gram;

                  return (
                    <tr key={i.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '12px' }}>{format(i.tarih, 'dd MMM HH:mm', { locale: tr })}</td>
                      <td style={{ padding: '12px' }}>{i.musteri?.isim?.trim() || '-'}</td>
                      <td style={{ padding: '12px' }}>{i.islemTuru}</td>
                      <td style={{ padding: '12px' }}>{i.kategori || '-'}</td>
                      <td style={{ padding: '12px' }}>{i.urun || '-'}</td>
                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>{finalAdet}</td>
                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>{finalGram.toFixed(2)}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{(i.nakit || 0).toFixed(2)}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{(i.havaleEft || 0).toFixed(2)}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{(i.krediKarti || 0).toFixed(2)}</td>
                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: i.islemTuru === 'Satış' ? 'green' : 'red' }}>
                        {(i.toplamTutar || 0).toFixed(2)} TL
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        )}
      </Paper>
      <style>{`

        /* --- TELEFON (<=560px): rapor sayfasi kisalsin ---
           Kategori tablolari alt alta uzayip 15-18 ekran suruyordu. Her
           kategori kendi icinde kayar hale geldi; sayfa birkac ekrana indi.
           Masaustune dokunulmaz. */
        @media (max-width: 560px) {
          /* Islem listesi tek basina 12.000px+ olabiliyor; kendi icinde kaysin */
          .hr-page .hr-transactions-wrap {
            max-height: 65vh !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch;
          }

          .hr-page .hr-category-table-wrap {
            max-height: 210px !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch;
          }

          .hr-page .hr-category-table th,
          .hr-page .hr-category-table td {
            padding: 2px 3px !important;
            font-size: 11px !important;
          }

          .hr-page .hr-category-paper {
            padding: 5px !important;
          }
        }
        /* --- MOBİL: Haftalık Raporlar (masaüstü sx stillerine dokunulmaz) --- */
        @media (max-width: 600px) {
          .hr-page {
            min-height: 0 !important;
            height: auto !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            overflow-x: hidden !important;
            overflow-y: visible !important;
            padding: 6px 2px 80px !important;
            box-sizing: border-box !important;
            border-radius: 8px !important;
          }

          .hr-header-paper,
          .hr-transactions-paper {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            overflow: hidden !important;
            box-sizing: border-box !important;
          }

          .hr-header-bar {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 10px !important;
            min-width: 0 !important;
          }

          .hr-header-bar .MuiTypography-root:first-of-type {
            font-size: 17px !important;
          }

          .hr-header-actions {
            justify-content: flex-end !important;
          }

          .hr-header-actions .MuiButton-root {
            flex: 1 !important;
            max-width: 120px !important;
            font-size: 12px !important;
            min-height: 36px !important;
          }

          .hr-summary-grid {
            grid-template-columns: 1fr !important;
            column-gap: 0 !important;
            row-gap: 10px !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
          }

          .hr-summary-grid > * {
            min-width: 0 !important;
            max-width: 100% !important;
          }

          .hr-summary-card {
            height: auto !important;
            min-height: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }

          .hr-summary-card-body {
            min-width: 0 !important;
            overflow: hidden !important;
          }

          .hr-summary-card-body .MuiTypography-root b {
            float: none !important;
            display: block !important;
            text-align: right !important;
            margin-top: 1px !important;
          }

          .hr-summary-card-body .MuiTypography-root:last-child {
            font-size: 14px !important;
            word-break: break-word !important;
          }

          .hr-stats-col {
            grid-template-columns: 1fr 1fr !important;
            gap: 8px !important;
            min-width: 0 !important;
          }

          .hr-stats-col .MuiPaper-root {
            height: auto !important;
            min-height: 56px !important;
            min-width: 0 !important;
            overflow: hidden !important;
          }

          .hr-pie-card {
            height: auto !important;
            min-height: 120px !important;
            max-width: 100% !important;
            overflow: hidden !important;
          }

          .hr-categories-title {
            font-size: 14px !important;
            margin-bottom: 8px !important;
          }

          .hr-categories-grid {
            grid-template-columns: 1fr !important;
            grid-auto-rows: auto !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            row-gap: 12px !important;
            margin-bottom: 16px !important;
          }

          .hr-category-item {
            grid-column: auto !important;
            grid-row: auto !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
          }

          .hr-category-paper {
            height: auto !important;
            min-height: 200px !important;
            max-height: 320px !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            overflow: hidden !important;
            box-sizing: border-box !important;
          }

          .hr-category-table-wrap {
            overflow-x: hidden !important;
            overflow-y: auto !important;
            max-width: 100% !important;
            min-width: 0 !important;
            -webkit-overflow-scrolling: touch;
          }

          .hr-category-table {
            width: 100% !important;
            min-width: 0 !important;
            table-layout: fixed !important;
          }

          .hr-category-table th:first-child,
          .hr-category-table td:first-child {
            width: 38% !important;
            max-width: 0 !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
          }

          .hr-category-table th:not(:first-child),
          .hr-category-table td:not(:first-child) {
            width: 10.3% !important;
            padding-left: 1px !important;
            padding-right: 1px !important;
            white-space: nowrap !important;
          }

          .hr-category-table th,
          .hr-category-table td {
            padding: 4px 2px !important;
            font-size: 10px !important;
          }

          .hr-transactions-wrap {
            overflow-x: auto !important;
            max-width: 100% !important;
            min-width: 0 !important;
            -webkit-overflow-scrolling: touch;
          }

          .hr-transactions-table {
            min-width: 920px !important;
          }

          .hr-transactions-table th,
          .hr-transactions-table td {
            padding: 8px 6px !important;
            font-size: 12px !important;
          }
        }
      `}</style>
    </Box>
  );
}